import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import Checkbox from '@material-ui/core/Checkbox';
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';

import AdminTable, { BORDER_BOTTOM, BORDER_TOP, modernTableProps, modernTableStyle } from './AdminTable';
import EmailLink from '../layout/EmailLink';
import ConfirmDeleteModal from '../modal/ConfirmDeleteModal';
import AddAccountUserModal from '../modal/AddAccountUserModal';
import DAO from '../../dao/DAO';

const DEFAULT_COLUMNS = 5;
const SEAT_HEADER_STYLE = { fontStyle: 'italic', textTransform: 'none', fontSize: 11, fontWeight: 'normal' };

const FAST_MODEL_PRODUCTS_SHOW_SEATS_DISABLED = new Set([
  'FastScout2', 'FastAccess'
]);

class AccountUserTable extends Component {
  static contextTypes = {
    dao: PropTypes.instanceOf(DAO),
    ngUser: PropTypes.object,
    onError: PropTypes.func,
    onSuccess: PropTypes.func,
  };

  static propTypes = {
    activations: PropTypes.array,
    onRevoke: PropTypes.object,
    accentColor: PropTypes.string
  }

  state = {
    editUserModal: false,
    editUser: {},
  }

  generateEmptyActivationRow = ({ rowStyle, style, cellStyle }) => {
    const defaultCellStyle = {
      ...cellStyle,
      borderLeft: 'inherit',
      borderRight: 'inherit',
      borderBottom: 'inherit',
    };
    return (
      <AdminTable.tr
        className="stat-table-row no-border-row"
        style={{ ...style, ...rowStyle }} cellStyle={cellStyle}
        key={0}>
        <AdminTable.td
          colspan={this.generateColSpan()}
          cellStyle={{ ...cellStyle, alignItems: 'center' }}
          style={defaultCellStyle}>
          <div
            style={{ height: 50 }}
            className='display-flex align-items-center justify-content-center color-gray'>
            NO USERS FOUND
          </div>
        </AdminTable.td>
      </AdminTable.tr>
    );
  }

  generateActivationRow = ({ accountUser, accentColor, timezone, rowStyle, style, cellStyle, accountProducts }) => {
    const defaultCellStyle = {
      ...cellStyle,
      borderLeft: 'inherit',
      borderRight: 'inherit',
      borderBottom: 'inherit',
    };

    return (
      <AdminTable.tr
        className="stat-table-row no-border-row"
        style={{ ...style, ...rowStyle }}
        cellStyle={cellStyle}
        key={accountUser.accountId}>
        <AdminTable.td
          cellStyle={cellStyle}
          style={defaultCellStyle}>
          <span>{`${accountUser.firstName} ${accountUser.lastName}`}</span>
        </AdminTable.td>
        <AdminTable.td
          cellStyle={cellStyle}
          style={defaultCellStyle}>
          <EmailLink email={accountUser.email} color={accentColor}/>
        </AdminTable.td>
        <AdminTable.td
          cellStyle={cellStyle}
          style={defaultCellStyle}>
          <span>{moment.tz(accountUser.startDate, timezone).format('MM/DD/YYYY')}</span>
        </AdminTable.td>
        <AdminTable.td
          cellStyle={{ ...cellStyle, alignItems: 'center' }}
          style={defaultCellStyle}>
          <Checkbox
            id={accountUser.id}
            color="primary"
            size="small"
            style={{ padding: 0, margin: 0 }}
            checked={accountUser.admin}
            onChange={this.handleAdmin}
          />
        </AdminTable.td>

        {accountProducts.map(product => {
          const cellContentFunction = product.showSeats ? this.generateUserSeats : this.generateUserRoles;
          return (
              <AdminTable.td cellStyle={{ ...cellStyle, alignItems: 'center' }} style={defaultCellStyle}>
                {cellContentFunction(product.key, accountUser.id)}
              </AdminTable.td>
          );
        }
        )}

        <AdminTable.td
          cellStyle={{ ...cellStyle, alignItems: 'center' }}
          style={defaultCellStyle}>
          <div className="align-items-baseline" style={{ paddingBottom: 0 }}>
              <span
                onClick={() => this.setState({ editUser: accountUser, editUserModal: true })}
                style={{ color: accentColor, fontSize: 20, paddingRight: 20, }}>
                <i className="fa fa-edit cursor-pointer"/>
              </span>
            <span
              onClick={() => this.setState({ deleteAccountUserId: accountUser.id, confirmDelete: true })}
              style={{ color: accentColor, fontSize: 20, paddingRight: 10 }}>
                <i className="fa fa-trash cursor-pointer"/>
              </span>
          </div>
        </AdminTable.td>
      </AdminTable.tr>
    );
  }

  deleteAccountUser = (id) => {
    const { dao, onSuccess, onError } = this.context;
    const { onUpdate } = this.props;
    dao.account.deleteAccountUser(id)
      .then(() => {
        onSuccess('User Deleted');
        this.setState({ confirmDelete: false });
        onUpdate();
      })
      .catch(onError);
  }

  handleAdmin = (e) => {
    const { dao } = this.context;
    const { onUpdate } = this.props;
    if (e.target.checked) {
      dao.account.promoteUser(e.target.id).then(() => onUpdate());
    } else {
      dao.account.demoteUser(e.target.id).then(() => onUpdate());
    }
  }

  generateUserSeats = (productName, userId) => {
    const { licensedSeats } = this.props;
    const seat = licensedSeats.filter((item) => item.user.id === userId && item.product === productName.toUpperCase());
    return seat && seat.length > 0 ? `${seat[ 0 ].qty} seat(s)` : '0 seat(s)';
  }

  generateUserRoles = (productName, userId) => {
    const { licensedSeats } = this.props;
    const seat = licensedSeats.filter((item) => item.user.id === userId && item.product === productName.toUpperCase());
    if (seat && seat.length > 0 && seat[ 0 ].roles) {
      return seat[ 0 ].roles.map((role) => role.name).join(', ');
    } else {
      return '-';
    }
  }

  findProduct(productName) {
    const { licensedProducts } = this.props;
    const product = licensedProducts.filter((item) => item.product.name === productName);
    return !!(product && product.length > 0);
  }

  generateProductTitle(productName) {
    const { licensedProducts } = this.props;
    const product = licensedProducts.filter((item) => item.product.name === productName);
    return product && product.length > 0 ? `${product[ 0 ].product.displayName || product[ 0 ].product.name}` : '';
  }

  generateSeatCount(productName) {
    const { licensedProducts } = this.props;
    const product = licensedProducts.filter((item) => item.product.name === productName);
    return product && product.length > 0 ? `(${product[ 0 ].allocated} out of ${product[ 0 ].quantity} used)` : '';
  }

  generateExpiryDate(productName) {
    const { licensedProducts } = this.props;
    const product = licensedProducts.filter((item) => item.product.name === productName);
    return product && product.length > 0 ?
      `Exp. ${moment(product[ 0 ].formatted_expiration_date).format('MMM D, YYYY')}` : '';
  }

  generateColSpan() {
    const { licensedProducts } = this.props;
    return (licensedProducts.length + DEFAULT_COLUMNS).toString();
  }

  getAccountProducts() {
    return _.chain(this.props.licensedProducts)
      .map(p => ({
        id: p.product.id,
        name: p.product.name,
        key: p.product.name.toUpperCase(),
        showSeats: !FAST_MODEL_PRODUCTS_SHOW_SEATS_DISABLED.has(p.product.name)
      }))
      .uniq(p => p.id)
      .value();
  }

  handleSaveEdit = (user) => {
    const { onSave } = this.props;
    const { editUser } = this.state;
    onSave({ userId: editUser.id, ...user }, true);
    this.setState({ editUserModal: false, editUser: {} });
  };

  filteredLicenseProducts() {
    const { licensedProducts } = this.props;
    return _.filter(licensedProducts, product => product.quantity - product.allocated >= 0);
  }

  render() {
    const { accentColor, accountUsers, scoutRoles, accessRoles } = this.props;
    const { confirmDelete, deleteAccountUserId, editUserModal, editUser } = this.state;
    const filteredLicenseProducts = this.filteredLicenseProducts();
    const STYLE = modernTableStyle(accentColor);
    const tableProps = modernTableProps(accentColor);
    const timezone = moment.tz.guess();
    const DEFAULT_HEADER_STYLE = {
      minHeight: 55,
      display: 'flex',
      padding: '5px 0',
      borderLeft: 'none',
      borderRight: 'none',
      ...STYLE.fastStatsTable.header,
      alignItems: 'center',
    };

    let activationRow = this.generateEmptyActivationRow({
      rowStyle: STYLE.fastStatsTable.row,
      style: { ...BORDER_TOP, fontWeight: 'bold' },
      cellStyle: STYLE.fastStatsTable.cell
    });


    const accountProducts = this.getAccountProducts();

    if (accountUsers !== null && accountUsers.length > 0) {
      activationRow = accountUsers.map(user => (
        this.generateActivationRow({
          accountUser: user,
          rowStyle: STYLE.fastStatsTable.row,
          cellStyle: STYLE.fastStatsTable.cell,
          accountProducts,
          accentColor,
          timezone
        })
      ));
    }

    const theme = createMuiTheme({
      palette: {
        primary: { 500: accentColor },
      },
    });

    return (
      <ThemeProvider theme={theme}>
        <div>
          <AddAccountUserModal
            featureFlags={this.props.featureFlags}
            open={editUserModal}
            scoutRoles={scoutRoles}
            accessRoles={accessRoles}
            accountUser={editUser}
            licensedProducts={filteredLicenseProducts}
            accentColor={accentColor}
            isEditing={true}
            onClose={() => this.setState({ editUserModal: false, editUser: {} })}
            onSave={this.handleSaveEdit}
          />
          <ConfirmDeleteModal
            onClose={() => this.setState({ confirmDelete: false })}
            onSave={() => this.deleteAccountUser(deleteAccountUserId)}
            open={confirmDelete}
            accentColor={accentColor}
            title={'Are you sure you want to delete user?'}
          />
          <AdminTable className="mg-top-sm" {...tableProps} style={{ width: '100%' }}>
            <AdminTable.thead tableAccent={STYLE.fastStatsTable.row.tableAccent}>
              <AdminTable.tr style={BORDER_BOTTOM} cellStyle={STYLE.fastStatsTable.cell} className={'stat-table-row'}>
                <AdminTable.th
                  cellStyle={STYLE.fastStatsTable.cell}
                  style={{ ...DEFAULT_HEADER_STYLE, fontWeight: 'bold' }}>Name</AdminTable.th>
                <AdminTable.th
                  cellStyle={STYLE.fastStatsTable.cell}
                  style={{ ...DEFAULT_HEADER_STYLE, fontWeight: 'bold' }}>Email</AdminTable.th>
                <AdminTable.th
                  cellStyle={STYLE.fastStatsTable.cell}
                  style={{ ...DEFAULT_HEADER_STYLE, fontWeight: 'bold' }}>Start Date</AdminTable.th>
                <AdminTable.th
                  cellStyle={{ ...STYLE.fastStatsTable.cell }}
                  style={{ ...DEFAULT_HEADER_STYLE, fontWeight: 'bold' }}>Admin</AdminTable.th>

                {accountProducts.map(product => (
                  <AdminTable.th cellStyle={STYLE.fastStatsTable.cell} style={DEFAULT_HEADER_STYLE}>
                    <div
                      className="display-flex full-width flex-direction-column align-items-center"
                      style={{ textAlign: 'center' }}>
                      <span style={{ fontWeight: 'bold' }}>{this.generateProductTitle(product.name)}</span>
                      <span style={SEAT_HEADER_STYLE}>{this.generateSeatCount(product.name)}</span>
                      <span style={SEAT_HEADER_STYLE}>{this.generateExpiryDate(product.name)}</span>
                    </div>
                  </AdminTable.th>
                ))}

                <AdminTable.th cellStyle={STYLE.fastStatsTable.cell} style={DEFAULT_HEADER_STYLE}>
                  <div style={{ textAlign: 'left' }}><span>&nbsp;</span></div>
                </AdminTable.th>
              </AdminTable.tr>
            </AdminTable.thead>
            <AdminTable.tbody tableAccent={STYLE.fastStatsTable.row.tableAccent}>
              {activationRow}
            </AdminTable.tbody>
          </AdminTable>
        </div>
      </ThemeProvider>
    );
  }
}

export default connect()(AccountUserTable);